"use client"

import { useEffect, useState } from "react"
import type { EarnedBadge } from "@/lib/badges/types"
import { getDefaultProfileImages } from "@/lib/default-profile-images"
import {
  DEFAULT_FRAMED_AVATAR_SCALE,
  FRAME_ADJ,
  FRAME_ADJ_OVERRIDES,
  getFrameAdj,
  normalizeFrameUrl,
  type FrameAdj,
  type RuntimeFrameTuning,
} from "@/lib/badge-frame-adj"

// Other files import these names from here.
export { DEFAULT_FRAMED_AVATAR_SCALE, FRAME_ADJ, FRAME_ADJ_OVERRIDES }

// Module-level cache so multiple avatars for the same user share one fetch
const frameCache = new Map<string, string | null>()

let runtimeFrameTuning: RuntimeFrameTuning | null = null
let runtimeFrameTuningPromise: Promise<RuntimeFrameTuning | null> | null = null

function loadRuntimeFrameTuning() {
  if (runtimeFrameTuning) return Promise.resolve(runtimeFrameTuning)
  if (runtimeFrameTuningPromise) return runtimeFrameTuningPromise

  runtimeFrameTuningPromise = fetch("/api/badge-frames/tuning")
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      if (!data || typeof data !== "object") return null
      runtimeFrameTuning = {
        avatarScale: typeof data.avatarScale === "number" ? data.avatarScale : undefined,
        base: data.base && typeof data.base === "object" ? data.base : {},
        sizeOverrides: data.sizeOverrides && typeof data.sizeOverrides === "object" ? data.sizeOverrides : {},
      }
      return runtimeFrameTuning
    })
    .catch(() => null)

  return runtimeFrameTuningPromise
}

// Earned-badge (corner overlay) tuning reuses the exact same storage/API as
// frame tuning — badge_frame_tuning's frame_key is just a free-text string,
// so a "badge:<slug>_r<rank>" key can live alongside real frame keys with no
// schema or API changes. Untuned badges (the vast majority — there are 60+)
// fall back to today's fixed proportions, same "new art, never measured yet"
// convention frames already use.
export const BADGE_ADJ_KEY_PREFIX = "badge:"
export function getBadgeAdjKey(slug: string, rank: number) {
  return `${BADGE_ADJ_KEY_PREFIX}${slug}_r${rank}`
}
export function getBadgeAdj(slug: string, rank: number, tuning?: RuntimeFrameTuning | null): FrameAdj {
  return tuning?.base?.[getBadgeAdjKey(slug, rank)] ?? { scale: 1, offsetX: 0, offsetY: 0 }
}

const BADGE_PRIORITY = [
  "founder",
  "goongod",
  "artgod", "scriptgod", "sonicgod", "vagod", "editgod",
  "omnicreator",
]

function pickFeaturedBadge(badges: EarnedBadge[]): EarnedBadge | null {
  let best: EarnedBadge | null = null
  let bestScore = -1
  for (const b of badges) {
    const priorityIdx = BADGE_PRIORITY.indexOf(b.slug)
    const score = priorityIdx >= 0 ? priorityIdx * 10 + b.rank : b.rank
    if (score > bestScore) { bestScore = score; best = b }
  }
  return best
}

interface AvatarWithFrameProps {
  src?: string
  fallback: string
  /** Explicit frame URL — takes priority over userId auto-fetch */
  frameUrl?: string | null
  /** Auto-fetch the user's equipped frame by userId */
  userId?: string
  size?: number
  className?: string
  /** Earned badges — highest priority one renders as a corner badge overlay */
  earnedBadges?: EarnedBadge[]
  /** Shrinks the avatar image inside the frame so thick rings do not cover the profile image. */
  avatarScale?: number
  /** Bypasses the FRAME_ADJ lookup with an explicit value — only for /frame-test, so it can
   * preview not-yet-committed tuning through this exact same component/CSS instead of a
   * hand-rolled copy that could quietly drift out of sync with the real render path.
   * `absolute: true` means offsetX/offsetY are already final pixels for this exact `size`
   * (skip the 160px-reference scaling) — used when previewing a per-size override. */
  adjOverride?: { scale: number; offsetX: number; offsetY: number; absolute?: boolean }
  /** Same bypass as adjOverride, but for the corner earned-badge overlay —
   * used by the admin tuner to preview not-yet-saved badge tuning. */
  badgeAdjOverride?: { scale: number; offsetX: number; offsetY: number }
  /** Companion/user avatarFocalX/Y (0-100) — where the source image gets
   * cropped from. Defaults to center, same as a plain object-cover. */
  focalX?: number
  focalY?: number
}

export function AvatarWithFrame({
  src,
  fallback,
  frameUrl: frameUrlProp,
  userId,
  size = 128,
  className,
  earnedBadges,
  avatarScale: avatarScaleProp,
  adjOverride,
  badgeAdjOverride,
  focalX = 50,
  focalY = 50,
}: AvatarWithFrameProps) {
  const [fetchedFrame, setFetchedFrame] = useState<string | null | undefined>(undefined)
  const [defaultAvatarUrl, setDefaultAvatarUrl] = useState<string | null>(null)
  const [tuning, setTuning] = useState<RuntimeFrameTuning | null>(runtimeFrameTuning)

  useEffect(() => {
    if (src) return
    let cancelled = false
    getDefaultProfileImages().then((config) => {
      if (!cancelled) setDefaultAvatarUrl(config.avatarUrl)
    })
    return () => { cancelled = true }
  }, [src])

  useEffect(() => {
    if (frameUrlProp !== undefined || !userId) return
    if (frameCache.has(userId)) {
      setFetchedFrame(frameCache.get(userId) ?? null)
      return
    }
    fetch(`/api/profile/${userId}`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        const url = data?.badgeFrameUrl ?? null
        frameCache.set(userId, url)
        setFetchedFrame(url)
      })
      .catch(() => { frameCache.set(userId, null); setFetchedFrame(null) })
  }, [userId, frameUrlProp])

  useEffect(() => {
    if (adjOverride) return
    let cancelled = false
    loadRuntimeFrameTuning().then((loaded) => {
      if (!cancelled && loaded) setTuning(loaded)
    })
    return () => { cancelled = true }
  }, [adjOverride])

  const frameUrl = normalizeFrameUrl(frameUrlProp !== undefined ? frameUrlProp : (fetchedFrame ?? null))
  const avatarScale = frameUrl ? (avatarScaleProp ?? tuning?.avatarScale ?? DEFAULT_FRAMED_AVATAR_SCALE) : 1
  const adj = adjOverride ?? (frameUrl ? getFrameAdj(frameUrl, size, tuning) : { scale: 1.4, offsetX: 0, offsetY: 0, absolute: false as const })
  const frameSize = Math.round(size * adj.scale)
  const avatarOffset = Math.round((frameSize - size) / 2)
  const avatarSize = Math.round(size * avatarScale)
  const avatarInset = Math.round((size - avatarSize) / 2)
  // Per-size overrides store final pixel offsets already tuned for this exact size — only
  // scale from the 160px reference when there's no override for this size.
  const scaledOffsetX = adj.absolute ? adj.offsetX : Math.round(adj.offsetX * (size / 160))
  const scaledOffsetY = adj.absolute ? adj.offsetY : Math.round(adj.offsetY * (size / 160))

  const featuredBadge = earnedBadges?.length ? pickFeaturedBadge(earnedBadges) : null
  const badgeAdj = badgeAdjOverride ?? (featuredBadge ? getBadgeAdj(featuredBadge.slug, featuredBadge.rank, tuning) : { scale: 1, offsetX: 0, offsetY: 0 })
  const badgeSize = Math.round(size * 0.35 * badgeAdj.scale)
  const badgeOffset = Math.round(badgeSize * 0.1)
  // Same 160px-reference scaling convention as frame offsets — a value tuned
  // at one avatar size stays visually consistent at every other size.
  // bottom/right shrink to move an element down/right, so offsets subtract.
  const badgeOffsetPxX = Math.round(badgeAdj.offsetX * (size / 160))
  const badgeOffsetPxY = Math.round(badgeAdj.offsetY * (size / 160))

  return (
    <div
      // box-content: some callers (e.g. the profile page hero avatar) pass a className with
      // a border (border-4). Tailwind's global reset makes every box border-box by default,
      // which means an absolutely-positioned child's top/left resolve against the *padding*
      // box — inset by the border width from this div's outer edge. Since every frame/avatar
      // position below is computed assuming a clean size×size box with nothing eating into
      // it, box-content forces the border to sit outside the declared width/height instead of
      // shrinking it, so a bordered context renders identically to a borderless one.
      className={`relative inline-block box-content${className ? ` ${className}` : ""}`}
      style={{ width: size, height: size }}
    >
      {/* Avatar circle */}
      <div
        className="absolute rounded-full overflow-hidden bg-background"
        style={{
          width: avatarSize,
          height: avatarSize,
          top: avatarInset,
          left: avatarInset,
        }}
      >
        {src ? (
          <img
            src={src}
            alt={fallback}
            className="w-full h-full object-cover"
            style={{ objectPosition: `${focalX}% ${focalY}%` }}
          />
        ) : defaultAvatarUrl ? (
          <img
            src={defaultAvatarUrl}
            alt={fallback}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-primary/10">
            <span className="text-primary font-bold text-2xl select-none">
              {fallback}
            </span>
          </div>
        )}
      </div>

      {/* Badge frame — sized and offset per FRAME_ADJ tuning */}
      {frameUrl && (
        <img
          src={frameUrl}
          alt=""
          aria-hidden="true"
          // max-w-none/max-h-none: Tailwind's preflight resets every <img> to
          // max-width:100%/height:auto, which silently clamps this element's width to its
          // parent's box (since the frame is deliberately bigger than the avatar it wraps) —
          // squishing it into a tall, narrow sliver instead of a square, no matter what the
          // inline width/height say. That's the actual cause of every "ring doesn't fit"
          // report — not a scale/offset tuning problem at all.
          className="absolute pointer-events-none z-10 max-w-none max-h-none"
          style={{
            width: frameSize,
            height: frameSize,
            top: -avatarOffset + scaledOffsetY,
            left: -avatarOffset + scaledOffsetX,
            objectFit: "contain",
          }}
        />
      )}

      {/* Earned badge overlay — bottom-right corner */}
      {featuredBadge && (
        <div
          className="absolute rounded-full ring-2 ring-background overflow-hidden z-20"
          style={{
            width: badgeSize,
            height: badgeSize,
            bottom: -badgeOffset - badgeOffsetPxY,
            right: -badgeOffset - badgeOffsetPxX,
          }}
          title={featuredBadge.name}
        >
          <img
            src={`/badges/${featuredBadge.slug}_r${featuredBadge.rank}.webp`}
            alt={featuredBadge.name}
            className="w-full h-full object-contain"
          />
        </div>
      )}
    </div>
  )
}
